<script lang="ts" setup>
	defineProps<{
		balance: number | string
		tipMessage?: string | number
		cardDetails: DebitCardPayload | null
	}>()
	const { addCard } = useCardDetails()
	const { hasAnyPermission } = reactor(usePermissionStore())
	const updateCard = () => {
		if (hasAnyPermission.value([PERMISSIONS.WALLET.ADD_CARD])) {
			addCard().then(e => {
				if (e) {
					refreshNuxtData('card-transaction')
				}
			})
			return null
		}
		useNotification().alert({
			status: 400,
			message: "You don't have permission to add a new card"
		})
	}
</script>

<template>
	<div
		class="min-h-[180px] w-full justify-between gap-3 rounded-md border border-gray-200 bg-white py-8 lg:py-0 xl:flex"
	>
		<div class="px-5 md:pt-8 xl:flex-1">
			<div class="relative mt-auto">
				<div class="flex items-center justify-between">
					<h1 class="font-medium text-gray-700">Wallet Balance</h1>

					<div class="mb-2 hidden items-center gap-x-3 gap-y-4 md:flex">
						<button
							v-if="hasAnyPermission([PERMISSIONS.WALLET.FUND])"
							type="button"
							data-testid="fund_wallet"
							class="default-btn group border-primary-2 bg-primary-2"
							@click="$emit('fund')"
						>
							<lazy-tools-icon
								dir="wallet"
								icon="wallet-2"
								path-class="stroke-white fill-transparent"
								:height="20"
								:width="20"
							/>

							<span class="text-white"> Fund wallet </span>
						</button>

						<nuxt-link
							v-if="hasAnyPermission([PERMISSIONS.WALLET.WITHDRAW])"
							data-testid="withdraw"
							class="default-btn group border border-primary-2 tracking-wide text-primary-2 disabled:border-gray-300 disabled:text-gray-300"
							to="/withdraw"
						>
							<lazy-tools-icon
								dir="wallet"
								icon="wallet-2"
								path-class="group-disabled:stroke-gray-300"
								:height="20"
								:width="20"
							/>
							Withdraw funds
						</nuxt-link>
					</div>
				</div>
				<a-tooltip color="white">
					<h3
						class="relative max-w-lg overflow-hidden text-ellipsis py-1.5 text-2xl font-semibold text-gray-700 sm:py-3 md:text-[44px]"
						data-testid="wallet-balance"
					>
						₦{{ hasAnyPermission([PERMISSIONS.WALLET.VIEW_BALANCE]) ? balance : '****' }}
					</h3>
					<template #title>
						<div class="relative font-medium text-blue-950 sm:p-2">
							₦{{ tipMessage && hasAnyPermission([PERMISSIONS.WALLET.VIEW_BALANCE]) ? balance : '*****' }}
						</div>
					</template>
				</a-tooltip>
				<span class="my-3 block text-xs font-medium tracking-wide text-gray-500">
					Only <span class="font-semibold text-primary-2">claims</span> and
					<span class="font-semibold text-primary-2">referrals paid</span> are eligible for withdrawal
				</span>
			</div>

			<div class="mt-4 flex w-full flex-wrap items-center gap-x-3 gap-y-4 md:hidden" data-mobile>
				<button
					v-if="hasAnyPermission([PERMISSIONS.WALLET.FUND])"
					type="button"
					class="default-btn group border-primary-2 bg-primary-2 hover:!bg-transparent"
					@click="$emit('fund')"
				>
					<lazy-tools-icon
						dir="wallet"
						icon="wallet-2"
						path-class="stroke-white fill-transparent"
						:height="20"
						:width="20"
					/>

					<span class="text-white"> Fund wallet </span>
				</button>

				<nuxt-link
					v-if="hasAnyPermission([PERMISSIONS.WALLET.WITHDRAW])"
					type="button"
					class="default-btn group border border-primary-2 text-primary-2 hover:bg-primary-2 hover:text-white"
					to="/withdraw"
				>
					<lazy-tools-icon
						dir="wallet"
						icon="wallet-2"
						path-class="group-hover:stroke-white"
						:height="20"
						:width="20"
					/>
					Withdraw funds
				</nuxt-link>
			</div>
		</div>

		<div class="my-5 hidden border-l border-gray-300 sm:block" />

		<div class="flex-1 border-gray-200 px-3 pt-8">
			<wallet-payment-card :card-details="cardDetails" class="min-h-[140px]" @add-card="updateCard" />
		</div>
	</div>
</template>

<style scoped>
	.default-btn {
		@apply flex h-[40px] max-w-xs items-center gap-2 rounded-md border px-2 text-xs font-medium transition;
	}

	.hide-balance {
		@apply before:absolute before:bottom-0 before:h-full before:w-full before:from-gray-200 before:to-gray-50 before:backdrop-blur-lg;
	}
</style>
